/**
 * CircleCropModal — Circle profile photo cropper.
 *
 * Allows the user to:
 *   1. See a preview of the uploaded image
 *   2. Pan and zoom to position their face
 *   3. Output a square canvas cropped to the visible circle region
 *
 * The visible area is a circle (CSS clip-path) but the output is a square PNG
 * with the circle region. The display layer renders it as a circle via border-radius.
 */

import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';

/* ------------------------------------------------------------------ */
/*  Constants                                                           */
/* ------------------------------------------------------------------ */

const OUTPUT_SIZE = 512;  // Output canvas size (square)
const VIEWPORT_SIZE = 280; // On-screen viewport diameter
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

/* ------------------------------------------------------------------ */
/*  Props                                                               */
/* ------------------------------------------------------------------ */

interface CircleCropModalProps {
  /** Object URL or data URL of the image to crop */
  imageSrc: string;
  /** Natural width of the source image */
  naturalW: number;
  /** Natural height of the source image */
  naturalH: number;
  /** Called with the cropped data URL (square PNG with circle content) */
  onConfirm: (dataUrl: string) => void;
  /** Called when user cancels */
  onCancel: () => void;
}

/* ------------------------------------------------------------------ */
/*  Styles                                                              */
/* ------------------------------------------------------------------ */

const S = {
  backdrop: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 200000,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },

  title: {
    color: 'var(--text-primary)',
    fontSize: 16,
    fontWeight: 600,
    margin: 0,
  },

  hint: {
    color: 'var(--text-tertiary)',
    fontSize: 13,
    margin: 0,
  },

  viewportContainer: {
    position: 'relative' as const,
    width: VIEWPORT_SIZE,
    height: VIEWPORT_SIZE,
    overflow: 'hidden',
    borderRadius: '50%',
    cursor: 'grab',
    border: '3px solid rgba(255,255,255,0.25)',
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
    touchAction: 'none' as const,
    userSelect: 'none' as const,
  },

  image: {
    position: 'absolute' as const,
    transformOrigin: '0 0',
    pointerEvents: 'none' as const,
  } as React.CSSProperties,

  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: 'var(--text-tertiary)',
    fontSize: 13,
  },

  slider: {
    width: 180,
    accentColor: 'var(--accent-primary)',
  },

  buttons: {
    display: 'flex',
    gap: 12,
  },

  btnCancel: {
    background: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: 6,
    padding: '8px 24px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },

  btnConfirm: {
    background: 'var(--accent-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export default function CircleCropModal({
  imageSrc,
  naturalW,
  naturalH,
  onConfirm,
  onCancel,
}: CircleCropModalProps) {
  const { t } = useAppTranslation('pages');
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);

  // Base scale to fill the circle viewport
  const baseScale = useMemo(() => {
    return Math.max(VIEWPORT_SIZE / naturalW, VIEWPORT_SIZE / naturalH);
  }, [naturalW, naturalH]);

  const displayScale = baseScale * zoom;

  const clampPan = useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const maxPanX = Math.max(0, (dw - VIEWPORT_SIZE) / 2);
      const maxPanY = Math.max(0, (dh - VIEWPORT_SIZE) / 2);
      return {
        x: Math.max(-maxPanX, Math.min(maxPanX, px)),
        y: Math.max(-maxPanY, Math.min(maxPanY, py)),
      };
    },
    [baseScale, naturalW, naturalH, zoom],
  );

  // ── Pointer drag ──
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPanX: pan.x,
        startPanY: pan.y,
      };
    },
    [pan],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPan(clampPan(dragRef.current.startPanX + dx, dragRef.current.startPanY + dy));
    },
    [clampPan],
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // ── Mouse wheel zoom ──
  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom - e.deltaY * 0.002));
      setZoom(next);
      setPan((p) => clampPan(p.x, p.y, next));
    },
    [zoom, clampPan],
  );

  const handleZoomSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseFloat(e.target.value);
      setZoom(next);
      setPan((p) => clampPan(p.x, p.y, next));
    },
    [clampPan],
  );

  // ── Confirm: render to canvas ──
  const handleConfirm = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    // Source region visible in the circular viewport
    const visSrcW = VIEWPORT_SIZE / displayScale;
    const visSrcH = VIEWPORT_SIZE / displayScale;

    const displayW = naturalW * displayScale;
    const displayH = naturalH * displayScale;

    const visSrcCX = (displayW / 2 - pan.x) / displayScale;
    const visSrcCY = (displayH / 2 - pan.y) / displayScale;

    // Make it square (circle inscribed in square)
    const srcSize = Math.max(visSrcW, visSrcH);
    const srcX = Math.max(0, Math.min(naturalW - srcSize, visSrcCX - srcSize / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcSize, visSrcCY - srcSize / 2));

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clip to circle
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // Draw the cropped region
    ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    onConfirm(canvas.toDataURL('image/png'));
  }, [displayScale, naturalW, naturalH, pan, onConfirm]);

  // ── Close on Escape ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Image position calculation
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;
  const imgLeft = (VIEWPORT_SIZE - displayW) / 2 + pan.x;
  const imgTop = (VIEWPORT_SIZE - displayH) / 2 + pan.y;

  return (
    <div style={S.backdrop} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <p style={S.title}>{t('circleCrop.title')}</p>
      <p style={S.hint}>{t('circleCrop.hint')}</p>

      <div
        style={S.viewportContainer}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
      >
        <img
          ref={imgRef}
          src={imageSrc}
          alt="Crop preview"
          style={{
            ...S.image,
            width: displayW,
            height: displayH,
            left: imgLeft,
            top: imgTop,
          }}
          draggable={false}
        />
      </div>

      <div style={S.controls}>
        <span>−</span>
        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={handleZoomSlider}
          style={S.slider}
        />
        <span>+</span>
      </div>

      <div style={S.buttons}>
        <button type="button" style={S.btnCancel} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" style={S.btnConfirm} onClick={handleConfirm}>
          Save Photo
        </button>
      </div>
    </div>
  );
}
